// filepath: apps/web/src/features/admin/PermissionsMatrixPage.tsx
//
// FR-UI-ADM-1 — main page composition for the entity-type CRUD
// permission matrix editor.
//
// Composes:
//   - usePermissionsMatrix + usePromotionPendingCount
//   - PendingReviewBanner (AC-3)
//   - PermissionsMatrixGrid (AC-1)
//   - MatrixConflictDialog (AC-4)
//   - StagingOnlyError (AC-2 — defense-in-depth server-triggered)
//
// Six error states are handled (FR-UI-CORE-10 + AC-6):
//   - loading: skeleton
//   - fetch_failure: error message
//   - permission_denied: error message
//   - stale_version: 409 → MatrixConflictDialog
//   - rate_limit: 429 → error message
//   - staging_only: 403 ENV_PRODUCTION → StagingOnlyError

import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import {
  useCreateRole,
  useDeleteRole,
  usePermissionsMatrix,
  usePromotionPendingCount,
  useSaveRoleMatrix,
} from "./queries";
import {
  PermissionsMatrixGrid,
  type CrudAction,
} from "./PermissionsMatrixGrid";
import { PendingReviewBanner } from "./PendingReviewBanner";
import { StagingOnlyError } from "./StagingOnlyError";
import { MatrixConflictDialog } from "./MatrixConflictDialog";
import type {
  MatrixState,
  PermissionsResponse,
  StagingOnlyError as StagingOnlyErrorT,
  VersionConflictError,
} from "./types";

interface PermissionsMatrixPageProps {
  /** Resolved environment kind. Drives read-only mode + StagingOnlyError. */
  environmentKind: string;
  /** Optional click-through to the FR-UI-PROM-1 diff page. */
  onOpenPromotion?: () => void;
}

export function PermissionsMatrixPage({
  environmentKind,
  onOpenPromotion,
}: PermissionsMatrixPageProps): React.ReactElement {
  const { t } = useTranslation();
  const matrixQuery = usePermissionsMatrix();
  const pendingQuery = usePromotionPendingCount();
  const saveMutation = useSaveRoleMatrix();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();

  // Local editable state (mirror of the matrix fetched from the server).
  // On each toggle we mutate the per-role permissions map. The page
  // resets to the server state on refetch.
  const [draft, setDraft] = useState<Record<string, PermissionsResponse>>({});
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);

  const data = matrixQuery.data;
  const effectiveMatrix: MatrixState | null = useMemo(() => {
    if (data === undefined) return null;
    const merged: Record<string, PermissionsResponse> = {};
    for (const role of data.roles) {
      merged[role.name] = draft[role.name] ??
        data.permissions[role.name] ?? {
          role: role.name,
          permissions: [],
          version: role.version,
        };
    }
    return { ...data, permissions: merged };
  }, [data, draft]);

  const handleCellToggle = useCallback(
    (
      roleName: string,
      entityType: string,
      action: CrudAction,
      isChecked: boolean,
    ) => {
      setDraft((prev) => {
        const base = prev[roleName] ??
          data?.permissions[roleName] ?? {
            role: roleName,
            permissions: [],
            version: data?.roles.find((r) => r.name === roleName)?.version ?? 0,
          };
        const next: PermissionsResponse = {
          ...base,
          permissions: updateGrant(
            base.permissions,
            entityType,
            action,
            isChecked,
          ),
        };
        return { ...prev, [roleName]: next };
      });
    },
    [data],
  );

  // ---- error-state mapping ----
  if (matrixQuery.isLoading) {
    return (
      <div
        className="space-y-4 animate-pulse p-6"
        data-testid="loading-skeleton"
      >
        <div className="h-12 bg-muted rounded" />
        <div className="h-32 bg-muted rounded" />
      </div>
    );
  }

  // Staging-only server reject (HTTP 403 ENV_PRODUCTION).
  const lastErr = saveMutation.error as Error | StagingOnlyErrorT | null;
  if (
    lastErr !== null &&
    lastErr !== undefined &&
    typeof lastErr === "object" &&
    "kind" in lastErr &&
    lastErr.kind === "staging_only"
  ) {
    return (
      <StagingOnlyError
        environmentKind={environmentKind}
        message={lastErr.message}
      />
    );
  }

  if (matrixQuery.error) {
    return (
      <ErrorState
        kind={classifyMatrixError(matrixQuery.error)}
        message={(matrixQuery.error as Error).message}
      />
    );
  }

  if (effectiveMatrix === null) {
    return <p className="text-muted-foreground text-sm p-6">No data.</p>;
  }

  const isProduction = environmentKind !== "staging";

  return (
    <div className="space-y-6 p-6" data-testid="permissions-matrix-page">
      <header>
        <h1 className="text-lg font-semibold">{t("admin.matrix.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("admin.matrix.subtitle")}
        </p>
      </header>

      <PendingReviewBanner
        pendingCount={pendingQuery.data ?? null}
        onOpenPromotion={() => onOpenPromotion?.()}
      />

      <PermissionsMatrixGrid
        roles={effectiveMatrix.roles}
        permissions={effectiveMatrix.permissions}
        entityTypes={effectiveMatrix.entityTypes}
        onCellToggle={handleCellToggle}
        isReadOnlyRole={(roleName) =>
          effectiveMatrix.roles.find((r) => r.name === roleName)?.is_system ??
          false
        }
        isPlatformColumn={(entityType) =>
          entityType === "*" || entityType === "auth"
        }
        isProduction={isProduction}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={isProduction || saveMutation.isPending}
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          onClick={async () => {
            // Save each role that has a draft. Sequential to keep
            // audit log order stable.
            setConflict(null);
            for (const role of effectiveMatrix.roles) {
              const draftRole = draft[role.name];
              if (draftRole === undefined) continue;
              try {
                await saveMutation.mutateAsync({
                  roleName: role.name,
                  version: draftRole.version,
                  permissions: draftRole,
                });
              } catch (err) {
                if (err && typeof err === "object" && "kind" in err) {
                  if ((err as { kind: string }).kind === "version_conflict") {
                    setConflict(err as VersionConflictError);
                    return;
                  }
                  if ((err as { kind: string }).kind === "staging_only") {
                    return;
                  }
                }
                return;
              }
            }
            setDraft({});
          }}
          data-testid="save-matrix-button"
        >
          {t("admin.matrix.action.save")}
        </button>
        <button
          type="button"
          disabled={isProduction || createRole.isPending}
          className="rounded border border-border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            const name = window.prompt(t("admin.matrix.action.new_role")) ?? "";
            if (name.trim() === "") return;
            createRole.mutate({ name: name.trim() });
          }}
          data-testid="new-role-button"
        >
          {t("admin.matrix.action.new_role")}
        </button>
        <button
          type="button"
          disabled={isProduction || deleteRole.isPending}
          className="rounded border border-destructive px-3 py-2 text-sm text-destructive disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            const roleName =
              window.prompt(t("admin.matrix.action.delete_role")) ?? "";
            if (roleName.trim() === "") return;
            if (
              !window.confirm(
                t("admin.matrix.confirm.delete_role") +
                  " (" +
                  roleName.trim() +
                  ")",
              )
            ) {
              return;
            }
            deleteRole.mutate({ roleName: roleName.trim() });
          }}
          data-testid="delete-role-button"
        >
          {t("admin.matrix.action.delete_role")}
        </button>
      </div>

      <MatrixConflictDialog
        open={conflict !== null}
        conflict={conflict}
        onClose={() => setConflict(null)}
        onRefetch={() => {
          setConflict(null);
          matrixQuery.refetch();
        }}
        onMerge={() => {
          // FR-UI-CORE-6 "merge" semantics: take the server's matrix
          // (already in conflict.serverMatrix) and overlay the user's
          // draft on top. We keep the user's draft intact but pin the
          // new server version so the next save attempt re-checks
          // concurrency.
          if (conflict === null) return;
          setDraft((prev) => {
            const next = { ...prev };
            for (const roleName of Object.keys(
              conflict.serverMatrix
                ? { [conflict.serverMatrix.role]: conflict.serverMatrix }
                : {},
            )) {
              const existing = next[roleName];
              const server = conflict.serverMatrix;
              if (existing === undefined || server === undefined) continue;
              next[roleName] = {
                ...existing,
                version: conflict.currentVersion,
                permissions: mergePermissions(
                  existing.permissions,
                  server.permissions,
                ),
              };
            }
            return next;
          });
          setConflict(null);
        }}
        onDiscard={() => {
          setDraft({});
          setConflict(null);
          matrixQuery.refetch();
        }}
      />
    </div>
  );
}

interface ErrorStateProps {
  kind:
    | "fetch_failure"
    | "permission_denied"
    | "rate_limit"
    | "stale_version"
    | "network";
  message: string;
}

function ErrorState({ kind, message }: ErrorStateProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="text-destructive text-sm p-6"
      role="alert"
      data-testid={`matrix-error-${kind}`}
    >
      {t(`admin.errors.${kind}`)} {message}
    </div>
  );
}

function classifyMatrixError(err: ApiError | Error): ErrorStateProps["kind"] {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return "permission_denied";
    if (err.status === 409) return "stale_version";
    if (err.status === 429) return "rate_limit";
    if (err.status === 0) return "network";
    if (err.status >= 500) return "fetch_failure";
  }
  return "fetch_failure";
}

function updateGrant(
  grants: PermissionsResponse["permissions"],
  entityType: string,
  action: string,
  isChecked: boolean,
): PermissionsResponse["permissions"] {
  const next: PermissionsResponse["permissions"] = [];
  let existingIndex = -1;
  for (let i = 0; i < grants.length; i++) {
    const g = grants[i];
    if (g === undefined) continue;
    if (g.entity_type === entityType) {
      existingIndex = i;
      const actions = g.actions.filter((a) => a !== action);
      if (isChecked) actions.push(action);
      if (actions.length > 0) {
        next.push({ entity_type: entityType, actions });
      }
    } else {
      next.push(g);
    }
  }
  if (existingIndex === -1 && isChecked) {
    next.push({ entity_type: entityType, actions: [action] });
  }
  return next;
}

function mergePermissions(
  user: PermissionsResponse["permissions"],
  server: PermissionsResponse["permissions"],
): PermissionsResponse["permissions"] {
  // FR-UI-CORE-6 "merge" semantics: server wins on conflicts; user's
  // grant is preserved if the server has no entry for that
  // (entity_type, action) tuple.
  const serverMap = new Map<string, Set<string>>();
  for (const g of server) {
    serverMap.set(g.entity_type, new Set(g.actions));
  }
  const merged: PermissionsResponse["permissions"] = [];
  const seen = new Set<string>();
  for (const g of user) {
    const serverActions = serverMap.get(g.entity_type);
    const effective = new Set<string>();
    if (serverActions !== undefined) {
      for (const a of serverActions) effective.add(a);
    }
    for (const a of g.actions) effective.add(a);
    merged.push({ entity_type: g.entity_type, actions: [...effective] });
    seen.add(g.entity_type);
  }
  for (const g of server) {
    if (!seen.has(g.entity_type)) {
      merged.push(g);
    }
  }
  return merged;
}
